import { useMemo } from "react";
import { useGetList } from "react-admin";
import { ResourceName, StoplossClaim } from "@/types";
import {
  IN_PROCESS_STATUSES,
  SUBMITTED_ONLY_STATUSES,
  REIMBURSED_STATUSES,
  DENIED_STATUSES,
  STOPLOSS_CLAIM_STATUS_GROUPS,
} from "@/constants/stoplossClaimsGroupings";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

export type MetricStat = {
  label: string;
  count: number;
  amount: number;
  link?: string;
  hint?: string;
};

/** Build date-range filter for a calendar year */
export const yearFilter = (year?: number): Record<string, string> => {
  if (!year) return {};
  return {
    created_datetime_gte: new Date(year, 0, 1).toISOString(),
    created_datetime_lt: new Date(year + 1, 0, 1).toISOString(),
  };
};

export const useStoplossClaimStats = (selectedYear?: number) => {
  const { canList } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  const baseFilter = useMemo(() => yearFilter(selectedYear), [selectedYear]);

  // Fetch all claims (lightweight — just need count + status + amount)
  const { data: allClaims = [], isLoading } = useGetList<StoplossClaim>(ResourceName.STOPLOSS_CLAIMS, {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "created_datetime", order: "DESC" },
    filter: baseFilter,
  });

  const stats = useMemo(() => computeClaimStats(allClaims, canList), [allClaims, canList]);

  return { stats, isLoading };
};

/** Pure computation — given claims and permission flag, produce the 5 metric stats. Exported for testing. */
export const computeClaimStats = (
  allClaims: Pick<StoplossClaim, "claim_status" | "final_cumulative_total">[],
  canList: boolean
): MetricStat[] => {
  const inProcess = allClaims.filter((c) => (IN_PROCESS_STATUSES as readonly string[]).includes(c.claim_status));
  const submitted = allClaims.filter((c) => (SUBMITTED_ONLY_STATUSES as readonly string[]).includes(c.claim_status));
  const reimbursed = allClaims.filter((c) => (REIMBURSED_STATUSES as readonly string[]).includes(c.claim_status));
  const denied = allClaims.filter((c) => (DENIED_STATUSES as readonly string[]).includes(c.claim_status));

  const sumAmount = (claims: Pick<StoplossClaim, "final_cumulative_total">[]) =>
    claims.reduce((sum, c) => sum + (c.final_cumulative_total || 0), 0);

  return [
    {
      label: "Total Claims",
      count: allClaims.length,
      amount: sumAmount(allClaims),
      hint: "All stop-loss claims across every status",
      ...(canList && { link: "/stoploss-claims?filter={}" }),
    },
    {
      label: "Needs Action",
      count: inProcess.length,
      amount: sumAmount(inProcess),
      hint: "Claims requiring review or action (New, In Process, Pended)",
      ...(canList && {
        link: `/stoploss-claims?filter=${JSON.stringify({ claim_status: STOPLOSS_CLAIM_STATUS_GROUPS.IN_PROCESS })}`,
      }),
    },
    {
      label: "Submitted",
      count: submitted.length,
      amount: sumAmount(submitted),
      hint: "Claims submitted to carrier, awaiting reimbursement decision",
      ...(canList && {
        link: `/stoploss-claims?filter=${JSON.stringify({ claim_status: SUBMITTED_ONLY_STATUSES })}`,
      }),
    },
    {
      label: "Reimbursed",
      count: reimbursed.length,
      amount: sumAmount(reimbursed),
      hint: "Claims fully or partially reimbursed by carrier",
      ...(canList && {
        link: `/stoploss-claims?filter=${JSON.stringify({ claim_status: REIMBURSED_STATUSES })}`,
      }),
    },
    {
      label: "Denied",
      count: denied.length,
      amount: sumAmount(denied),
      hint: "Claims denied by carrier",
      ...(canList && { link: `/stoploss-claims?filter=${JSON.stringify({ claim_status: DENIED_STATUSES })}` }),
    },
  ];
};
